import { quantityProposalExportLabels } from "$root/functional-areas/instruments-editor/quantity/proposal-columns";
import { userClassifications$ } from "$root/functional-areas/instruments/user-classifications";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { formatDate } from "$root/localization/formatters";
import type * as smartWorkspaces from "@eu.mdotm.api/smart-workspaces";
import type { Currencies, UserInstrumentClassificationDto } from "@eu.mdotm.api/sphere";
import { readAtom } from "@mdotm/shadcn/legacy/reactivity";
import { unreachable } from "@mdotm/utils/runtime";
import { t } from "i18next";
import {
	columnIdToConcept,
	COMPOSITION_COLUMN_CONCEPTS,
	type CompositionColumnConceptsKeys,
	INSTRUMENT_IDENTITY_COL_IDS,
	withUuid,
} from "./concepts";
import { buildSortCriteria, orderedVisibleColIds, readPersistedColumns } from "./sorting";

type CompositionSnapshot = "CURRENT" | "PROPOSAL";

/**
 * Composition flavour passed to the data provider. `AGGREGATE` looks through nested portfolios to
 * their underlying instruments; `STANDARD` keeps the direct (exact) composition. Only investments
 * have an aggregate view.
 */
type CompositionType = "STANDARD" | "AGGREGATE";

/** Kind of entity whose composition is being exported. */
type CompositionExportEntityType = "INVESTMENT" | "INVESTMENT_HISTORY" | "REFERENCE";

type CompositionExportConfigBase = {
	investmentUuid: string;
	snapshot: CompositionSnapshot;
	/**
	 * `persistedColStateKeys` entry of the table whose layout drives the export. A quantity-mode
	 * investment export ignores it and reads the quantity grid's own key instead — see
	 * `quantityMode` on the investment configs.
	 */
	colStateKey: string;
	/**
	 * Proposal modification date used to label the pre-proposal weight column (`PREVIOUS_WEIGHT`) to
	 * match the composition table's "Composition {date}" header. Only meaningful for the `PROPOSAL`
	 * snapshot — `PREVIOUS_WEIGHT` never appears in a `CURRENT` export — and may be omitted to mirror
	 * tables that render the header without a date.
	 */
	proposalDate?: string;
};

/**
 * Quantity-mode (MDOT-24700) flag: `true` when the investment's composition is expressed in
 * quantities, which is what the composition tables key their quantity variant off. It picks the
 * `colStateKey` the export reads (the quantity grids persist under their own keys) and the concept
 * the proposal grid's weight column maps to — see `QUANTITY_COL_STATE_KEYS` and
 * `resolveCompositionColumns`.
 */
type QuantityModeConfig = {
	quantityMode?: boolean;
	/** The portfolio's base currency, named in a quantity proposal's market-value label as in its table. */
	currency?: Currencies | null;
};

/**
 * An investment's composition can be exported either as its direct holdings (`STANDARD`) or by
 * looking through nested portfolios to their underlying instruments (`AGGREGATE`).
 */
type InvestmentCompositionExportConfig = CompositionExportConfigBase &
	QuantityModeConfig & {
		compositionType: CompositionType;
		/**
		 * As-of date (`YYYY-MM-DD`), sent as the data provider's `asOfDate` parameter so the export reflects
		 * the stored snapshot for that day. Required but nullable: a surface that cannot be pinned passes
		 * `undefined`, so each export states its stance on the date. `asOf` and `history` are two ways of
		 * asking for the past, one by calendar date and one by recorded action, and the data provider
		 * rejects a request carrying both, so each lives only on the config of the entity type that sends it.
		 */
		asOf: string | undefined;
	};

/** References never have an aggregate (look-through) view, so they are always exported as `STANDARD`. */
type ReferenceCompositionExportConfig = CompositionExportConfigBase;

/**
 * Exports a point-in-time composition from an investment's history. `history` (the history event
 * UUID) is required and `compositionType` is intentionally absent: a historical snapshot has no
 * look-through (aggregate) variant, so the data provider's `compositionType` parameter is not sent.
 */
type InvestmentHistoryCompositionExportConfig = CompositionExportConfigBase &
	QuantityModeConfig & {
		/** History event UUID, passed through as the data provider's `history` parameter. */
		history: string;
	};

/** Weight column ids per snapshot, used to build the default (all-columns-visible) composition order. */
const COMPOSITION_WEIGHT_COL_IDS: Record<CompositionSnapshot, ReadonlyArray<string>> = {
	CURRENT: ["WEIGHT"],
	PROPOSAL: ["PREVIOUS_WEIGHT", "ENHANCED_WEIGHT", "DELTA_WEIGHT"],
};

/**
 * The same, for a quantity-mode composition, in the order of `columnsDefinition.instrumentQuantityComposition`
 * / `instrumentQuantityProposalComposition`. The proposal's previous and delta quantity and market-value
 * columns have no concept, so `columnIdToConcept` drops them from the export.
 */
const COMPOSITION_QUANTITY_COL_IDS: Record<CompositionSnapshot, ReadonlyArray<string>> = {
	CURRENT: ["QUANTITY", "MARKET_PRICE", "MARKET_VALUE", "WEIGHT"],
	PROPOSAL: [
		"WEIGHT",
		"PREVIOUS_WEIGHT",
		"DELTA_WEIGHT",
		"PREVIOUS_MARKET_VALUE",
		"DELTA_MARKET_VALUE",
		"QUANTITY",
		"PREVIOUS_QUANTITY",
		"DELTA_QUANTITY",
		"MARKET_PRICE",
		"MARKET_VALUE",
	],
};

/** Col-state key the quantity-mode composition grids persist under, per snapshot. */
const QUANTITY_COL_STATE_KEYS: Record<CompositionSnapshot, string> = {
	CURRENT: persistedColStateKeys["tableV4-investment-quantityComposition-read"],
	PROPOSAL: persistedColStateKeys["tableV5-investment-quantityProposalComposition-read"],
};

/** Default column order (all columns visible) mirroring the composition table for the snapshot. */
function defaultCompositionColumnOrder(
	snapshot: CompositionSnapshot,
	classifications: UserInstrumentClassificationDto[],
	quantityMode: boolean,
): string[] {
	return [
		...INSTRUMENT_IDENTITY_COL_IDS,
		...(quantityMode ? COMPOSITION_QUANTITY_COL_IDS : COMPOSITION_WEIGHT_COL_IDS)[snapshot],
		...classifications.map((c) => c.classificationUuid).filter((uuid): uuid is string => uuid != null),
	];
}

/**
 * Resolves the composition table's persisted column preference (stored under `colStateKey`) into the
 * ordered, visible export concepts keyed by their AG Grid `colId`, plus the matching sort criteria. The
 * projection mirrors what the user sees: visibility and order follow the persisted state
 * (`orderedVisibleColIds`), and sorting follows the persisted sort criteria mapped onto the projected
 * concepts (`buildSortCriteria`). With no stored preference it falls back to the default all-columns
 * layout for `snapshot` and no sort. Shared by the single-investment composition export
 * (`buildCompositionRequest`) and the bulk composition export (`userCompositionColumnConcepts`).
 */
function resolveCompositionColumns(
	snapshot: CompositionSnapshot,
	colStateKey: string,
	quantityMode: boolean,
): {
	conceptByColId: Map<string, smartWorkspaces.ParameterizedConceptDTO>;
	sorting: smartWorkspaces.SortCriterionDTO[];
} {
	const classifications = readAtom(userClassifications$);
	const persistedColumns = readPersistedColumns(colStateKey);
	const columnOrder = persistedColumns
		? orderedVisibleColIds(persistedColumns)
		: defaultCompositionColumnOrder(snapshot, classifications, quantityMode);

	// Resolve each visible column to its concept, keyed by colId so persisted sort criteria can be
	// mapped back to the concept UUIDs they reference.
	const conceptByColId = new Map<string, smartWorkspaces.ParameterizedConceptDTO>();
	for (const colId of columnOrder) {
		const concept = columnIdToConcept(colId, classifications);
		if (concept) {
			conceptByColId.set(colId, concept);
		}
	}
	// The quantity proposal grid renders the proposal's weight under the plain `WEIGHT` colId, so that
	// column exports as the proposal-weight concept: on the `PROPOSAL` snapshot
	// `instrument:composition:weight` returns the pre-proposal weight, which `PREVIOUS_WEIGHT` already
	// projects. Remapped before the sort criteria are built, so a sort on that column follows it.
	if (quantityMode && snapshot === "PROPOSAL" && conceptByColId.has("WEIGHT" satisfies CompositionColumnConceptsKeys)) {
		conceptByColId.set("WEIGHT", withUuid(COMPOSITION_COLUMN_CONCEPTS.ENHANCED_WEIGHT));
	}
	const sorting = persistedColumns ? buildSortCriteria(persistedColumns, conceptByColId) : [];
	return { conceptByColId, sorting };
}

/**
 * The user's composition-table columns (instrument identity, weight and classification columns) as
 * export concepts, mirroring the composition table exactly: which columns appear, their order,
 * visibility and sort are all driven by the persisted column preferences under `colStateKey`, just like
 * `buildCompositionRequest`. With no stored preference the default all-columns layout for `snapshot` is
 * returned with no sort. Every projected concept carries the fresh `uuid` it was resolved with, and the
 * returned `sorting` references those same uuids — so callers must project these concepts without
 * reassigning their uuids for the sort to line up.
 */
export function userCompositionColumnConcepts(
	snapshot: CompositionSnapshot,
	colStateKey: string,
): { concepts: smartWorkspaces.ParameterizedConceptDTO[]; sorting: smartWorkspaces.SortCriterionDTO[] } {
	// The bulk provider serves weight compositions only, so the quantity layout never applies here.
	const { conceptByColId, sorting } = resolveCompositionColumns(snapshot, colStateKey, false);
	return { concepts: [...conceptByColId.values()], sorting };
}

/**
 * Builds the data request used to export an entity's composition through the smart-workspace data
 * provider (`exportWorkspaceData`). `entityType` discriminates the kind of entity and the config it
 * requires, dispatching to a per-entity resolver so the aggregate view can never be requested for a
 * reference: an `INVESTMENT` config carries `compositionType` (`STANDARD` direct holdings vs
 * `AGGREGATE` look-through of nested portfolios), while a `REFERENCE` is always exported as
 * `STANDARD`. An `INVESTMENT_HISTORY` config targets a point-in-time composition via a required
 * `history` event UUID and sends no `compositionType` at all (historical snapshots have no aggregate
 * view). `config.investmentUuid` targets the entity; `config.snapshot` selects the current
 * composition (single weight column) or the enhancement proposal (current / proposal / delta weight
 * columns); `config.colStateKey` is the `persistedColStateKeys` entry of the table whose layout
 * should drive the export (e.g. `persistedColStateKeys["tableV3-investment-composition-read"]`).
 * An investment carrying `quantityMode` exports the quantity composition instead: its grid's own
 * col-state key, its quantity/price/market-value columns, and its weight column read as the
 * proposal's.
 */
export function buildCompositionExportConfig(
	entityType: "INVESTMENT",
	config: InvestmentCompositionExportConfig,
): smartWorkspaces.DataRequestDTO;
export function buildCompositionExportConfig(
	entityType: "INVESTMENT_HISTORY",
	config: InvestmentHistoryCompositionExportConfig,
): smartWorkspaces.DataRequestDTO;
export function buildCompositionExportConfig(
	entityType: "REFERENCE",
	config: ReferenceCompositionExportConfig,
): smartWorkspaces.DataRequestDTO;
export function buildCompositionExportConfig(
	entityType: CompositionExportEntityType,
	config: CompositionExportConfigBase & QuantityModeConfig & { compositionType?: CompositionType },
): smartWorkspaces.DataRequestDTO {
	switch (entityType) {
		// The public overloads guarantee an `INVESTMENT` call supplies `compositionType`.
		case "INVESTMENT":
			return resolveInvestmentCompositionExport(config as InvestmentCompositionExportConfig);
		case "INVESTMENT_HISTORY":
			return resolveInvestmentHistoryCompositionExport(config as InvestmentHistoryCompositionExportConfig);
		case "REFERENCE":
			return resolveReferenceCompositionExport(config);
		default:
			return unreachable(entityType);
	}
}

/**
 * Investment export: the caller chooses the composition flavour — `STANDARD` (direct holdings) or
 * `AGGREGATE` (look-through of nested portfolios to their underlying instruments).
 */
function resolveInvestmentCompositionExport(config: InvestmentCompositionExportConfig): smartWorkspaces.DataRequestDTO {
	return buildCompositionRequest(config, { compositionType: config.compositionType, asOf: config.asOf });
}

/**
 * Investment history export: a point-in-time snapshot identified by `config.history`. No
 * `compositionType` is sent, since historical snapshots have no look-through (aggregate) variant, and
 * no `asOfDate`, since the data provider rejects it alongside `history`.
 */
function resolveInvestmentHistoryCompositionExport(
	config: InvestmentHistoryCompositionExportConfig,
): smartWorkspaces.DataRequestDTO {
	return buildCompositionRequest(config, { history: config.history });
}

/** Reference export: references have no aggregate view, so the composition is always `STANDARD`. */
function resolveReferenceCompositionExport(config: ReferenceCompositionExportConfig): smartWorkspaces.DataRequestDTO {
	return buildCompositionRequest(config, { compositionType: "STANDARD" });
}

/**
 * Label for the pre-proposal weight column (`PREVIOUS_WEIGHT`), matching the composition table header
 * `TABLE.HEADERS.COMPOSITION_AT` used by `columnsDefinition.instrumentProposalComposition`:
 * "Composition {date}", where the date is the proposal's modification date with the year omitted. When
 * no date is supplied the header renders without one, exactly like the table.
 */
function previousWeightLabel(proposalDate?: string): string {
	return t("TABLE.HEADERS.COMPOSITION_AT", {
		date: proposalDate ? formatDate(proposalDate, { omitYear: true }) : "",
	});
}

/**
 * Shared core that turns a base config plus the resolved composition flavour into the smart-workspace
 * data request. The exported concepts mirror the composition table the user sees: they are derived
 * from the user's persisted column preferences (visibility + order) stored under `colStateKey`,
 * falling back to the default all-columns layout when no preference is stored. `ACTION` is never
 * exported. Sorting mirrors the table's persisted sort criteria, resolved against the projected
 * concepts.
 */
function buildCompositionRequest(
	{
		investmentUuid,
		snapshot,
		colStateKey,
		proposalDate,
		quantityMode = false,
		currency,
	}: CompositionExportConfigBase & QuantityModeConfig,
	{ compositionType, history, asOf }: { compositionType?: CompositionType; history?: string; asOf?: string },
): smartWorkspaces.DataRequestDTO {
	const { conceptByColId, sorting } = resolveCompositionColumns(
		snapshot,
		// The table the export mirrors: a quantity-mode composition renders its own grid, persisted under
		// its own key, so the caller's `colStateKey` (the weight table's) would project a layout that
		// holds no quantity columns at all.
		quantityMode ? QUANTITY_COL_STATE_KEYS[snapshot] : colStateKey,
		quantityMode,
	);

	// The pre-proposal weight column's label is date-dependent, so it is resolved here (against the
	// proposal date) rather than baked into the static concept catalog — mirroring the composition
	// table's "Composition {date}" header.
	const previousWeightConcept = conceptByColId.get("PREVIOUS_WEIGHT");
	if (previousWeightConcept) {
		previousWeightConcept.label = previousWeightLabel(proposalDate);
	}
	if (quantityMode && snapshot === "PROPOSAL") {
		const labels = quantityProposalExportLabels(proposalDate, currency);
		conceptByColId.forEach((concept, colId) => {
			concept.label = labels[colId] ?? concept.label;
		});
	}
	const concepts = [...conceptByColId.values()];

	return {
		concepts,
		dataProvider: {
			dataProviderId: "userInstrumentCompositionByInvestment",
			parameters: {
				investmentUuid: investmentUuid as unknown as object,
				snapshot: snapshot as unknown as object,
				...(compositionType ? { compositionType: compositionType as unknown as object } : {}),
				...(history ? { history: history as unknown as object } : {}),
				...(asOf ? { asOfDate: asOf as unknown as object } : {}),
			},
		},
		projection: concepts.map((c) => c.uuid!),
		sorting:
			sorting.length > 0
				? sorting
				: [
						{
							direction: "DESC",
							parameterizedConceptUuid: conceptByColId.get("WEIGHT" satisfies CompositionColumnConceptsKeys)?.uuid,
						},
					],
	};
}
